import React from 'react'
import { useSelector } from 'react-redux'
import { Trans, useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet'
import { useThemeChanger } from 'cityads-v3/hooks/theme'

import Balaclava from 'cityads-v3/components/Balaclava'
import Box from '@mui/material/Box'
import Card from 'cityads-v3/components/Card/Card'
import DocumentationForm from 'cityads-v3/components/DocumentationForm'
import PageWithMenu from 'cityads-v3/components/PageWithMenu'
import Typography from '@mui/material/Typography'

import PermContactCalendarOutlinedIcon from '@mui/icons-material/PermContactCalendarOutlined'
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import { Light as SyntaxHighlighter } from 'react-syntax-highlighter'
import http from 'react-syntax-highlighter/dist/esm/languages/hljs/http'
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json'
import bash from 'react-syntax-highlighter/dist/esm/languages/hljs/bash'
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml'
import sunburst from 'react-syntax-highlighter/dist/esm/styles/hljs/sunburst'
import vs from 'react-syntax-highlighter/dist/esm/styles/hljs/vs'

import RemoteAuth from './RemoteAuth'
import OAuth20 from './OAuth20'

import classes from './Api.scss'
import { RootState } from 'cityads-v3/types/reducer'

SyntaxHighlighter.registerLanguage('http', http)
SyntaxHighlighter.registerLanguage('json', json)
SyntaxHighlighter.registerLanguage('bash', bash)
SyntaxHighlighter.registerLanguage('xml', xml)

export default function Api(): JSX.Element {
  const { t } = useTranslation(['Publisher'])
  const language = useSelector((state: RootState) => state.language.language)
  const themeChanger = useThemeChanger()
  const docsLanguage = language === 'ru' ? 'ru' : 'en'
  const isDark = themeChanger.theme.palette.mode === 'dark'

  return (
    <>
      <Helmet>
        <title>{t('api.api')}</title>
      </Helmet>

      <Balaclava
        title={t('api.api')}
      />

      <PageWithMenu>
        <Card
          title={t('api.oauth20')}
          classes={{
            root: classes.apiCard
          }}
          data-page-menu-key={'oauth20'}
          data-page-menu-icon={<ConfirmationNumberOutlinedIcon />}
          data-page-menu-title={t('api.oauth20')}
        >
          <DocumentationForm
            documentationTitle={t('api.header2')}
            documentationContent={(
              <>
                <Typography variant={'body2'} gutterBottom className={classes.body2}>
                  {t('api.text2')}
                </Typography>
                <Typography variant={'body2'} className={classes.body2}>
                  <Trans t={t} i18nKey={'api.text3'}>
                    For authorized access to the API, get an access token according to the documentation,
                    or simply download the prepared Cityads OAuth 2.0 library to quickly start using our API:
                    <a
                      href="https://github.com/CityAds/api-client"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      PHP 5.6+
                      <OpenInNewIcon classes={{ root: classes.openInNewIcon }} />
                    </a>
                  </Trans>
                </Typography>
              </>
            )}
          >
            <OAuth20 />
          </DocumentationForm>
        </Card>

        <Card
          title={t('api.accessToken.title')}
          classes={{
            root: classes.apiCard
          }}
          data-page-menu-key={'access_token'}
          data-page-menu-icon={<ConfirmationNumberOutlinedIcon />}
          data-page-menu-title={t('api.accessToken.title')}
        >
          <DocumentationForm
            documentationContent={
              <>
                <Typography variant="body2" paragraph>
                  {t('api.accessToken.p1')}
                  <br />
                  {t('api.accessToken.p2')}
                </Typography>

                <Typography variant="subtitle1" paragraph>
                  {t('api.accessToken.p3')}
                </Typography>

                <SyntaxHighlighter
                  language="http"
                  style={isDark ? sunburst : vs}
                >
                  {`POST https://auth2.cityads.com/oauth/access_token HTTP/1.1

{
  "client_id": "client_id_string",
  "client_secret": "client_secret_string",
  "grant_type": "client_credentials"
}`}

                </SyntaxHighlighter>

                <Box mt={4} />
                <Typography variant="subtitle1" gutterBottom>
                  {t('api.accessToken.p4')}
                </Typography>

                <SyntaxHighlighter
                  language="bash"
                  style={isDark ? sunburst : vs}
                >
                  {`
curl -L -X POST \
-H "Content-Type:application/json" \
-d \
'{
  "client_id": "client_id_string",
  "client_secret": "client_secret_string",
  "grant_type": "client_credentials"
}' \
'https://auth2.cityads.com/oauth/access_token'
`}
                </SyntaxHighlighter>

                <Typography variant={'subtitle1'} gutterBottom>
                  {t('api.accessToken.p5')}
                </Typography>

                <Typography variant="body2">
                  <table>
                    <tbody>
                      <tr>
                        <td>client_id</td>
                        <td>{t('api.accessToken.client_id')}</td>
                      </tr>
                      <tr>
                        <td>client_secret</td>
                        <td>{t('api.accessToken.client_secret')}</td>
                      </tr>
                      <tr>
                        <td>grant_type</td>
                        <td>{t('api.accessToken.grant_type')}</td>
                      </tr>
                    </tbody>
                  </table>
                </Typography>

                <Box mt={4} />

                <Typography variant={'subtitle1'} gutterBottom>
                  {t('api.accessToken.p6')}
                </Typography>

                <Typography variant="body2">
                  {t('api.accessToken.p7')}
                </Typography>

                <SyntaxHighlighter
                  language="json"
                  style={isDark ? sunburst : vs}
                >
                  {`
{
  "access_token": "mmMy2dZEeoMm13Fr7X6uc8TaP5262Qjin717P67Z",
  "token_type": "Bearer",
  "expires_in": 3600
}
`}

                </SyntaxHighlighter>

                <Typography variant={'subtitle1'} gutterBottom>
                  {t('api.accessToken.p8')}
                </Typography>

                <Typography variant="body2">
                  <table>
                    <tbody>
                      <tr>
                        <td>access_token</td>
                        <td>{t('api.accessToken.access_token')}</td>
                      </tr>
                      <tr>
                        <td>token_type</td>
                        <td>{t('api.accessToken.token_type')}</td>
                      </tr>
                      <tr>
                        <td>expires_in</td>
                        <td>{t('api.accessToken.expires_in')}</td>
                      </tr>
                    </tbody>
                  </table>
                </Typography>
              </>
            }
          />
        </Card>

        <Card
          title={t('api.useAccessToken.title')}
          classes={{
            root: classes.apiCard
          }}
          data-page-menu-key={'use_access_token'}
          data-page-menu-icon={<ConfirmationNumberOutlinedIcon />}
          data-page-menu-title={t('api.useAccessToken.title')}
        >
          <DocumentationForm
            documentationContent={
              <>
                <Typography variant="body2" gutterBottom>
                  {t('api.useAccessToken.p1')}
                </Typography>

                <Typography variant="body2" gutterBottom>
                  {t('api.useAccessToken.p2')}
                </Typography>
              </>
            }
          />
        </Card>

        <Card
          title={t('api.quoting.title')}
          classes={{
            root: classes.apiCard
          }}
          data-page-menu-key={'quoting'}
          data-page-menu-icon={<ConfirmationNumberOutlinedIcon />}
          data-page-menu-title={t('api.quoting.title')}
        >
          <DocumentationForm
            documentationContent={
              <>
                <Typography variant="body2" gutterBottom>
                  {t('api.quoting.p1')}
                  <br />
                  {t('api.quoting.p2')}
                  <ul>
                    <li>{t('api.quoting.type1')}</li>
                    <li>{t('api.quoting.type2')}</li>
                  </ul>

                  {t('api.quoting.p3')}
                </Typography>

                <Typography variant="body2" paragraph>
                  {t('api.quoting.p4')}
                </Typography>
              </>
            }
          />
        </Card>

        <Card
          title={t('api.remoteAuth')}
          classes={{
            root: classes.apiCard
          }}
          data-page-menu-key={'remote_auth'}
          data-page-menu-icon={<PermContactCalendarOutlinedIcon />}
          data-page-menu-title={t('api.remoteAuth')}
          expandable={true}
          expandclick={['head']}
          collapsedSize="0px"
        >
          <DocumentationForm
            documentationTitle={t('api.header1')}
            documentationLink={`/api/dev/webmaster/offers?lang=${docsLanguage}`}
            documentationContent={(
              <Typography variant={'body2'} gutterBottom className={classes.body2}>
                <Trans t={t} i18nKey={'api.text11'}>
                  It’s not being supported or improved anymore, can even break or stop working one day. It’s still avaliable if you want to continue using it. The documentation can be found
                  <a href={`/api/dev/interface/rest?lang=${docsLanguage}`}>
                    here
                  </a>.
                </Trans>
              </Typography>
            )}
          >
            <RemoteAuth />
          </DocumentationForm>
        </Card>
      </PageWithMenu>
    </>
  )
}
